import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

/** The service worker's ability to serve the current app shell and configured static assets without a network. */
export type OfflineReadinessStatus =
    'checking' | 'downloading' | 'ready' | 'update-ready' | 'unavailable' | 'error';

interface OfflineReadinessState {
    error: string | null;
    status: OfflineReadinessStatus;
}

const initialState: OfflineReadinessState = {
    error: null,
    status: 'checking',
};

/**
 * Shared, signal-based service-worker readiness state for the shell. The update service owns the browser API
 * subscription and reports its lifecycle here, keeping UI consumers independent of `SwUpdate` implementation details.
 */
export const OfflineReadinessStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed((store) => ({
        isReady: computed(() => store.status() === 'ready' || store.status() === 'update-ready'),
        isUpdateReady: computed(() => store.status() === 'update-ready'),
        label: computed(() => {
            switch (store.status()) {
                case 'ready':
                    return 'Ready for offline use';
                case 'update-ready':
                    return 'Update ready for offline use';
                case 'downloading':
                    return 'Downloading offline content';
                case 'checking':
                    return 'Checking offline availability';
                case 'unavailable':
                    return 'Offline use unavailable';
                case 'error':
                    return 'Offline availability needs attention';
            }
        }),
    })),
    withMethods((store) => ({
        checking: (): void => patchState(store, { error: null, status: 'checking' }),
        downloading: (): void => patchState(store, { error: null, status: 'downloading' }),
        failed: (error: unknown): void =>
            patchState(store, {
                error: error instanceof Error ? error.message : 'The offline cache could not be updated.',
                status: 'error',
            }),
        ready: (): void => patchState(store, { error: null, status: 'ready' }),
        unavailable: (): void => patchState(store, { error: null, status: 'unavailable' }),
        updateReady: (): void => patchState(store, { error: null, status: 'update-ready' }),
    }))
);
